<script>
  import { ComponentType } from '../../../../../../js/constants';
  import { getNavState } from '../../../../../../js/stores';
  import { hoverWithDelay } from '../../../../../../js/hoverWithDelay';
  import { onClickOutside } from '../../../../../../js/onClickOutside';
  import { onTouchOrClick } from '../../../../../../js/onTouchOrClick';
  import { getCategory, getSubcategory, getNavDeviceType } from '../../../../../../js/trackingUtils';
  import { trackCdp } from '../../../../../../js/tracking';

  import Flyout, { DEFAULT_WIDTH as FLYOUT_DEFAULT_WIDTH } from '../../Flyout/Flyout.svelte';
  import CategoryList from './CategoryList/CategoryList.svelte';

  // Props
  export let id;
  export let title;
  export let navItems = [];
  export let alignment = 'left';
  export let desktopMode;

  // Constants
  const TIMEOUT_ENTER_DURATION = 100;
  const TIMEOUT_LEAVE_DURATION = 300;
  const FLYOUT_WITH_DESCRIPTIONS_WIDTH = 280;
  const FLYOUT_SUBCATEGORY_OPEN_WIDTH = 500;
  const FLYOUT_SUBCATEGORY_OPEN_WIDTH_NO_DESCRIPTIONS = 460;
  const navStateStore = getNavState();
  const trackData = { menuId: id, menuText: title };

  // Variables
  let element;
  let isOpen = false;

  // Reactive declarations
  $: hasDescriptions = navItems.some((item) => item.description);
  $: isCurrentCategory = $navStateStore.category === element;
  $: hasCurrentSubcategory = isCurrentCategory && Boolean($navStateStore.subcategory);
  $: flyoutMinWidth = hasDescriptions ? FLYOUT_WITH_DESCRIPTIONS_WIDTH : FLYOUT_DEFAULT_WIDTH;
  $: flyoutSubcategoryOpenWidth = hasDescriptions
    ? FLYOUT_SUBCATEGORY_OPEN_WIDTH
    : FLYOUT_SUBCATEGORY_OPEN_WIDTH_NO_DESCRIPTIONS;
  $: flyoutWidth = hasCurrentSubcategory ? flyoutSubcategoryOpenWidth : flyoutMinWidth;

  // Open the flyout
  function open() {
    if (isOpen) {
      return;
    }
    isOpen = true;
    $navStateStore = {
      ...$navStateStore,
      category: element, // Set current category
      subcategory: null, // Clear current subcategory
    };
    trackCdp({
      eventName: 'Open Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.MAIN_NAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  // Close the flyout
  function close() {
    if (!isOpen) {
      return;
    }
    isOpen = false;
    trackCdp({
      eventName: 'Close Menu',
      properties: {
        ...trackData,
        menuCategory: getCategory(element),
        menuSubcategory: getSubcategory(element),
        menuLocation: ComponentType.MAIN_NAVIGATION,
        navDeviceType: getNavDeviceType(element),
      },
    });
  }

  // Toggle the flyout
  function toggle() {
    isOpen ? close() : open();
  }

  // Ensure only one flyout is open at a time.
  $: if (isOpen && !isCurrentCategory) {
    close();
  }
</script>

<div
  {id}
  class="Category"
  class:isOpen
  data-category={title}
  data-component-type={ComponentType.MAIN_NAVIGATION_MENU}
  bind:this={element}
  use:hoverWithDelay={[open, close, TIMEOUT_ENTER_DURATION, TIMEOUT_LEAVE_DURATION]}
  use:onClickOutside={close}
>
  <button
    class="toggle nav-button"
    class:isOpen
    aria-haspopup="true"
    aria-expanded={isOpen ? true : false}
    use:onTouchOrClick={toggle}
  >
    <span class="title">{title}</span>
  </button>
  {#if desktopMode}
    <Flyout {alignment} {isOpen} width={flyoutWidth}>
      <CategoryList {navItems} />
    </Flyout>
  {/if}
</div>

<style>@charset "UTF-8";
.Category {
  box-sizing: border-box;
  z-index: 1;
  position: relative;
  display: inline-block;
  margin: 0;
  padding: 0;
}
.Category.isOpen {
  z-index: 2;
}

.toggle.nav-button {
  cursor: pointer;
  appearance: none;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;
  display: inline-block;
  width: auto;
  height: auto;
  min-height: 0;
  max-height: none;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 10px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--color-text, #1a1a1a);
  font-family: "Square Sans Text VF", "Square Sans Text", "Helvetica", "Arial", "Hiragino Kaku Gothic Pro", "ヒラギノ角ゴ Pro W3", "メイリオ", meiryo, "ＭＳ Ｐゴシック", sans-serif;
  font-size: 16px;
  line-height: 28px;
  font-weight: var(--font-weight-medium, 500);
  letter-spacing: normal;
  text-align: center;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
  padding: 11px 10px 9px;
  transition: none;
}
.toggle.nav-button:focus {
  outline: none;
}
.toggle.nav-button::-moz-focus-inner {
  border: 0;
}
.toggle.nav-button:focus {
  outline: none;
}
.toggle.nav-button::before {
  content: "";
  pointer-events: none;
  display: block;
  position: absolute;
  inset: 0;
  background-color: var(--color-highlight, rgba(26, 26, 26, 0.1));
  border-radius: 0;
  opacity: 0;
  transition: opacity 100ms cubic-bezier(0.4, 0, 0.2, 1), transform 100ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(0);
}
.toggle.nav-button:focus:not(:active)::before {
  opacity: 1;
  transform: scale(1);
}
.toggle.nav-button.isOpen::before {
  opacity: 1;
  transform: scale(1);
}
.toggle.nav-button > * {
  position: relative;
}</style>
